/**
 * Design tokens for the AR Reporting Dashboard.
 *
 * Deliberately free of any MUI import. `constants.ts`, the chart components and
 * the status helpers all need these values, and routing them through `theme.ts`
 * meant every module that touched a status colour pulled the whole MUI theme in
 * with it — including in unit tests, where it dominated import time.
 *
 * `colors`, `typography`, `borderRadius` and `shadows` are the MRO brand tokens
 * from `MicroUI.ConfigurationManagementSystem/src/theme.ts`, copied value for
 * value (that file has no build step or package to import from directly, so
 * copying keeps this app's dependency graph free of the CMS repo). Keep these
 * in sync by hand if the source changes.
 *
 * `status` is not part of that palette — CMS has no notion of per-file-status
 * colour, since AR request statuses are specific to this app's domain. Carried
 * over unchanged from the legacy fhir-dashboard app.
 */
export const tokens = {
  colors: {
    white: '#FFFFFF',
    black: '#000000',
    primary: '#0153FF',
    primaryHover: '#004AE6',
    secondary: '#01A4FF',
    success: '#16711F',
    error: '#D32F2F',
    warning: '#FF9800',
    info: '#C1DAEE',
    background: '#F1F3F4',
    paper: '#FFFFFF',
    darkGrey: '#4B555C',
    grey: '#A6ABAD',
    lightGrey: '#E6E6E6',
    divider: '#ccc',
    tableHeaderBg: '#EAF4FC',
    tableBorder: '#C1DAEE',
    primaryText: 'rgba(0, 0, 0, 0.87)',
    secondaryText: 'rgba(0, 0, 0, 0.54)',
  },
  /** Chart series colours, keyed by the file-status label they represent. */
  status: {
    patientNotFound: '#ffa500',
    multiplePatientsFound: '#800080',
    encounterNotFound: '#ffff00',
    documentNotFound: '#A52A29',
    fyiFlagFound: '#808080',
    failed: '#ff0000',
    processed: '#008000',
    inQueue: '#90ee90',
    inProgress: '#add8e6',
    patientMismatch: '#db7093',
    manualProcessingRequired: '#d3d3d3',
  },
  typography: {
    fontFamily: ['Inter', 'sans-serif'].join(','),
  },
  borderRadius: {
    small: '4px',
    button: '6px',
    medium: '8px',
    large: '12px',
    extraLarge: '14px',
  },
  shadows: {
    low: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    medium: '0px 4px 6px rgba(0, 0, 0, 0.15)',
    high: '0px 6px 12px rgba(0, 0, 0, 0.2)',
  },
} as const;
